import { useLocalization } from '@lib/localization';
import { Box } from '@mui/material';
import { type ReactNode } from 'react';
import { type BadgeTone, filledBadgeColors, tableText, toneColors } from './DataTable.styles';

interface StatusBadgeProps {
  label: string;
  tone: BadgeTone;
  /**
   * A dot before plain text, for a column whose every row says something; without it a soft
   * filled label, for a mark only a few rows carry.
   */
  dot?: boolean;
  /** Stands before the label, as a lock does for what nobody here can change. */
  icon?: ReactNode;
  /** The full sentence, on hover, for a label too short to say it all. */
  title?: string;
}

/** A state in words and a colour, never the colour alone. */
export const StatusBadge = ({ label, tone, dot, icon, title }: StatusBadgeProps) => {
  const color = toneColors[tone];

  if (dot) {
    return (
      <Box component="span" title={title} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
        {icon}
        {label}
      </Box>
    );
  }

  return (
    <Box
      component="span"
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 20,
        px: 1,
        borderRadius: '6px',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1,
        ...filledBadgeColors(tone),
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {label}
    </Box>
  );
};

const week = 7 * 24 * 60 * 60 * 1000;

/**
 * How long ago for the last week, the day itself before it (with its year when that is not this
 * one), and the moment itself on hover. `empty` is said, quietly, where there is no date. The
 * folders' list view says how long ago at any age (`DateText` in `FolderItems`).
 */
export const DateCell = ({ value, empty }: { value?: string; empty?: string }) => {
  const { currentLanguage } = useLocalization();

  if (!value) return empty ? <Box component="span" sx={{ color: tableText.tertiary }}>{empty}</Box> : null;

  const date = new Date(value);
  const text = Date.now() - date.getTime() < week
    ? date.toAgoString()
    : date.toLocaleDateString(currentLanguage, {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    });

  return <span title={date.toLocaleString(currentLanguage)}>{text}</span>;
};
